import React, { Component } from 'react'
import { connect } from 'react-redux'
import DeviceInfo from 'react-native-device-info'
import SafeAreaInsets from 'safe-area-insets'
import _ from 'lodash'

import {
  statusBarStyles,
  keyboardBehaviors,
  sourceTypes,
  LOCATION_INPUT,
  dataTypes,
} from '@adalo/constants'

import {
  StatusBar,
  Dimensions,
  Platform,
  ScrollView,
  FlatList,
  StyleSheet,
  View,
  Keyboard,
  LogBox,
} from 'react-native'

import { createDataNode } from 'utils/variableHeight/buildPushTree'

import { hasDataAction } from 'utils/action-filtering'
import { Alert } from 'utils/alerts'
import { getDependencies } from 'utils/dependencies'
import { isScreenManagedDependency } from 'utils/dependencies/dataBindings'
import { getInputsDifferent } from 'utils/dependencies/formInputs'
import { executeAction } from 'utils/actions'
import { normalizeColor, defaultBranding } from 'utils/colors'
import { isMobileWeb } from 'utils/system'
import { isListObject } from 'utils/lists'
import { isCloseToBottom } from 'utils/scrolling'
import { removeExternalCollections } from 'utils/trial'
import { searchForSource } from 'utils/sources'
import { searchForInput, searchForField } from 'utils/inputs'
import { getDeviceType } from 'utils/device'
import { getMarginBottom } from 'utils/fixed'

import { fetch } from 'ducks/data'
import { getFormInputs } from 'ducks/formInputs'
import { setCurrentLocation } from 'ducks/location'
import {
  getNodeHeight,
  generatePushTree,
  getScreenPushTree,
} from 'ducks/pushTreeMap'

import { getScreenSize } from 'utils/responsive'
import { disableFeature } from 'utils/featureAccess'
import {
  finishTransaction,
  sentryTracingInterceptor,
  startTransaction,
} from 'utils/tracing'
import { scheduleRecurringTask } from 'utils/taskScheduler'
import { getAndroidDiagnosticInfo } from 'utils/diagnostics'
import ObjectRenderer from './ObjectRenderer'
import KeyboardAvoidingView from './KeyboardAvoidingView'
import ListBody from './ListBody'
import BackgroundImage from './BackgroundImage'
import { isPWA } from '../utils/analytics'
import AppContext from '../contexts/AppContext'
import Loading from './Loading'
import { getBindingsFunc } from '../utils/bindingUtils'

const DEPENDENCY_POLLING_INTERVAL = 10000

const RESPONSIVE_WEB_APPS_ALPHA_VERSION = '0.3'

// Patch to use a custom VirtualList container instead of ScrollView (which might have some issues)
// TODO(enzo): we need to do some testing around this to see what would be the best alternative
const USE_VIRTUAL_LIST_CONTAINER = false

const APPLY_UPDATES_INTERVAL = 300

const APPLY_UPDATES_THROTTLE = 300

//  Used only for applying updates right after a dependency finished fetching (to not wait for the next polling)
const APPLY_UPDATES_DEBOUNCE = 600

if (APPLY_UPDATES_THROTTLE > APPLY_UPDATES_INTERVAL) {
  console.warn(
    `APPLY_UPDATES_THROTTLE shouldn't be bigger than APPLY_UPDATES_INTERVAL`
  )
}

const PAGE_LIMIT = 20

const getBinding = () => null
const getBindings = () => ({})
const getBindingsList = () => []

class Screen extends Component {
  static contextType = AppContext

  constructor(props, context) {
    super(props, context)
    const { updateContext, getParams = () => {} } = context
    const { topScreen } = props
    this.getParams = getParams.bind(this)
    this.getBindingsFunc = () => getBindingsFunc(this)

    if (topScreen) {
      updateContext(this.getChild(), this.setContextConfigured.bind(this))
    }
  }

  state = {
    contextConfigured: false,
    updateVersion: 0,
    offset: 0,
    loading: false,
    loaded: false,
    refreshing: false,
    reachedPaginationEnd: false,
    scrollEnabled: true,
    initialRender: true,
    dimScreenWidth: getScreenSize().width,
    dimScreenHeight: getScreenSize().height,
    isPWA: isPWA(),
  }

  pollTeardowns = []

  pendingUpdates = []

  dimensionsEventListener = null

  setContextConfigured() {
    this.setState({ contextConfigured: true })
  }

  get screenUsesLocation() {
    const { component } = this.props

    // Search for bindings (Magic Text, Formula, etc.) that depend on device location data
    let usesLocation = searchForSource(
      component.dataBindings,
      sourceTypes.DEVICE_LOCATION
    )

    // Location inputs need to access device location data for biased suggestions
    if (!usesLocation) {
      usesLocation = searchForInput(component.objects, LOCATION_INPUT)
    }

    // Location fields need to access device location data for biased suggestions
    if (!usesLocation) {
      usesLocation = searchForField(component.objects, dataTypes.LOCATION)
    }

    return usesLocation
  }

  /** @returns {import('../types').Screen} component */
  // eslint-disable-next-line react/destructuring-assignment
  getComponent = () => this.props.component

  getChild() {
    return {
      setDirty: this.setDirty,
      getBinding,
      getBindings,
      getBindingsList,
      getComponent: this.getComponent,
      setVisible: this.setVisible,
      setHeight: this.setHeight,
    }
  }

  setListItems = (pushId, object, value) => {
    this.addToUpdates(pushId, object, { listItems: value })
  }

  setVisible = (pushId, object, value, visibleOnDevice) => {
    this.addToUpdates(pushId, object, { visible: value, visibleOnDevice })
  }

  setHeight = (pushId, object, value) => {
    this.addToUpdates(pushId, object, { height: Math.round(value) })
  }

  addToUpdates = (pushId, object, update) => {
    this.pendingUpdates.push({
      pushId,
      object,
      update,
    })
  }

  addDeviceChangedUpdate = device => {
    this.pendingUpdates.push({ type: 'deviceChanged', device })
  }

  listenToUpdates = () => {
    if (this.updatesInterval) {
      this.clearUpdateListener()
    }

    this.debouncedApplyUpdates() // Apply update before starting
    // Then apply on intervals, apply them throttled
    this.updatesInterval = scheduleRecurringTask(
      this.throttledApplyUpdates,
      APPLY_UPDATES_INTERVAL
    )
  }

  clearUpdateListener = () => {
    if (this.updatesInterval) {
      this.updatesInterval()
    }
  }

  applyUpdates = () => {
    const { dataMap: stateDataMap, component, generateNewPushTree } = this.props

    const hasUpdates = this.pendingUpdates.length
    if (!hasUpdates) return

    const newDataMap = _.cloneDeep(stateDataMap)

    while (this.pendingUpdates.length) {
      const nextUpdate = this.pendingUpdates.shift()
      if (nextUpdate?.type === 'deviceChanged') {
        continue
      }

      const { pushId, object, update } = nextUpdate

      let dataNode = newDataMap[pushId]

      if (!dataNode) {
        dataNode = createDataNode(pushId, object)
      }

      newDataMap[pushId] = {
        ...dataNode,
        ...update,
      }
    }
    const { body, fixed } = component.layout
    const { deviceType } = this.state

    generateNewPushTree({
      // body & fixed components are separated for rendering, but we need to generate push tree for all components,
      // fixed/sticky objects don't push each other, but their children can
      objects: [...body, ...fixed],
      dataMap: newDataMap,
      screenId: component.id,
      device: deviceType,
    })
  }

  throttledApplyUpdates = _.throttle(this.applyUpdates, APPLY_UPDATES_THROTTLE)

  debouncedApplyUpdates = _.debounce(
    this.throttledApplyUpdates,
    APPLY_UPDATES_DEBOUNCE
  )

  async startTransaction(name, data = {}) {
    const { component } = this.props
    const { getApp } = this.context
    const { id: appId, Organization } = getApp()
    const { id: componentId } = component

    return startTransaction({
      name,
      op: name,
      data: {
        ...data,
        appId,
        componentId,
        organizationId: Organization?.id,
        platform: Platform.OS,
        ...(Platform.OS === 'android' && {
          ...(await getAndroidDiagnosticInfo()),
        }),
      },
    })
  }

  setDirty = () => {
    this.setState(({ updateVersion }) => ({
      updateVersion: updateVersion + 1,
    }))
  }

  _getDependenciesWithoutTracing = () => {
    const { component, formInputs } = this.props
    const { getStore, getDatasources, getApp } = this.context
    const state = getStore().getState()

    const app = getApp()

    const { Organization } = app
    const { enabledFeatures } = Organization

    const disableExternalCollections = disableFeature(
      enabledFeatures,
      'customIntegrations'
    )

    let dependencies = []

    try {
      dependencies = getDependencies(component, {
        formInputs,
        getDatasources,
        getApp,
        state,
        getParams: this.getParams,
      })

      if (disableExternalCollections) {
        dependencies = removeExternalCollections(getDatasources, dependencies)
      }
    } catch (err) {
      Alert.alert(
        'Failed to load dependencies',
        'Try checking your authentication configuration and make sure your ' +
          'user is properly authenticated before reaching this page.',
        [{ text: 'OK', onPress: () => {} }]
      )
      console.log('ERROR FETCHING DEPENDENCIES:', err)
    }

    return dependencies
  }

  getDependencies = sentryTracingInterceptor(
    this._getDependenciesWithoutTracing,
    'getDependencies'
  )

  _handleRefreshWithoutTracing = async (skipPolling = true) => {
    this.setState({
      offset: 0,
      refreshing: true,
      reachedPaginationEnd: false,
    })

    const dependencies = await this.getDependencies()
    await this.fetchDependencies(dependencies, skipPolling)

    this.setState({ refreshing: false })
  }

  handleRefresh = async (skipPolling = true) => {
    const transaction = await this.startTransaction('Screen::handleRefresh', {
      skipPolling,
    })
    try {
      const result = await this._handleRefreshWithoutTracing(skipPolling)
      transaction?.setStatus('ok')
      return result
    } catch (err) {
      transaction?.setStatus('unknown_error')
      throw err
    } finally {
      transaction?.finish()
    }
  }

  _fetchDependenciesWithoutTracing = async (
    dependencies,
    skipPolling = false
  ) => {
    this.teardown()
    const { component } = this.props

    // Dependencies for Table objects are handled by the Table & PaginatedFetchComponent classes
    const screenDependencies = dependencies.filter(d =>
      isScreenManagedDependency(component, d)
    )

    await Promise.all(
      screenDependencies.filter(dep => dep.fetchFirst).map(this.fetchDependency)
    ).then(() => setTimeout(this.debouncedApplyUpdates, 100))

    const promises = screenDependencies.map(dep => {
      if (dep.listenForChanges && !skipPolling) {
        this.startPolling(dep)
      }

      return this.fetchDependency(dep)
    })

    if (this.screenUsesLocation) {
      const { setCurrentLocation } = this.props
      const { getBaseURL, getApp } = this.context
      const baseUrl = getBaseURL()
      const { id } = getApp()
      promises.push(setCurrentLocation(baseUrl, id, true))
    }

    return Promise.all(promises)
  }

  // Figure out what data is needed and fetch it
  fetchDependencies = sentryTracingInterceptor(
    this._fetchDependenciesWithoutTracing,
    'fetchDependencies'
  )

  fetchDependency = (dependency, offset = 0) => {
    const {
      fetch,
      component: { id: componentId },
    } = this.props

    const { getBaseURL, appVersion, getStore } = this.context
    const { offset: prevOffset } = this.state

    if (offset === 0 && prevOffset > 0) {
      offset = prevOffset
    }

    if (dependency?.paginate || dependency?.manualPagination) {
      dependency = { ...dependency, offset, limit: PAGE_LIMIT }
    }
    const dependencyPayload = {
      ...dependency,
      datasource: this.getDatasource(dependency.datasourceId),
      componentId,
      bindingIds: dependency.bindingId || [],
      appVersion,
      state: getStore().getState(),
      getParams: this.getParams,
    }

    return fetch(getBaseURL(), dependencyPayload)
  }

  getDatasource = datasourceId => {
    const { getDatasources } = this.context
    const datasources = getDatasources()

    return datasources[datasourceId]
  }

  startPolling = dependency => {
    const teardownPoll = scheduleRecurringTask(
      () => this.fetchDependency(dependency),
      DEPENDENCY_POLLING_INTERVAL,
      true
    )

    this.pollTeardowns.push(teardownPoll)
  }

  _setupWithoutTracing = async () => {
    let { component, topScreen } = this.props
    component = component || {}

    const prevDependencies = await this.getDependencies()
    await this.fetchDependencies(prevDependencies)

    const futureDependencies = await this.getDependencies()
    const newDependencies = futureDependencies.filter(futureDep =>
      prevDependencies.some(
        prevDep =>
          prevDep.id &&
          prevDep.id === futureDep.id &&
          (prevDep.columnFilter !== futureDep.columnFilter ||
            prevDep.sortReference !== futureDep.sortReference)
      )
    )

    if (newDependencies.length > 0) {
      await this.fetchDependencies(newDependencies)
    }

    this._loaded = true
    this.setState({ loaded: false })
    window.setTimeout(() => {
      this.setState({ loaded: true })
    }, 10)

    const { componentActions, onVisit } = component

    const isInitialMount = !this.previouslyMounted
    this.previouslyMounted = true

    const returning = this.backActionTaken
    this.backActionTaken = false

    if (
      (isInitialMount || returning) &&
      onVisit &&
      componentActions &&
      topScreen
    ) {
      const { actionId } = onVisit
      const action = componentActions[actionId]

      await this.executeAction(componentActions[actionId])

      const reloadRequired = hasDataAction(action)

      if (reloadRequired) {
        this.handleRefresh(false)
      }
    }

    if (!this.useFlatList) {
      this.checkPagination()
    }
  }

  setup = async () => {
    const transaction = await this.startTransaction('Screen::setup')
    try {
      const result = await this._setupWithoutTracing()

      transaction?.setStatus('ok')
      return result
    } catch (err) {
      transaction?.setStatus('unknown_error')
      transaction?.setData('error', err && err.message)
      throw err
    } finally {
      if (transaction !== undefined) {
        finishTransaction(transaction)
      }
    }
  }

  _executeActionWithoutTracing = async action => {
    if (!action) {
      return
    }

    await executeAction(action, {
      ...this.context,
      ...this.getChild(),
      screen: true,
      ...this.getBindingsFunc(),
    })
  }

  executeAction = sentryTracingInterceptor(
    this._executeActionWithoutTracing,
    'executeAction'
  )

  teardown = () => {
    this.pollTeardowns.forEach(pollTeardown => pollTeardown())

    this.pollTeardowns = []
  }

  isResponsiveWebApp = () => {
    const { component } = this.props

    const { layoutVersion } = component

    return layoutVersion === RESPONSIVE_WEB_APPS_ALPHA_VERSION
  }

  componentDidMount() {
    // TODO(enzo): We need to test if the patch with VirtualizedList is a good alternative for not having a ScrollView as container
    // In the meantime, we ignore the warnings
    if (!USE_VIRTUAL_LIST_CONTAINER && LogBox) {
      // Can't avoid having VirtualizedLists in FlatList
      LogBox.ignoreLogs(['VirtualizedLists should never be nested'])
    }

    this.setup()

    this.keyboardListener = Keyboard.addListener(
      'keyboardDidShow',
      this.handleResize
    )

    this.dimensionsEventListener = Dimensions.addEventListener(
      'change',
      this.screenSizeChange
    )

    window.setTimeout(this.handleResize, 100)

    if (this.isResponsiveWebApp()) {
      const { getApp } = this.context
      const app = getApp()

      this.listenToUpdates()
      const deviceType = getDeviceType(undefined, app)
      this.setState({ deviceType })
    }
  }

  componentWillUnmount() {
    if (this.isResponsiveWebApp()) {
      this.clearUpdateListener()
    }
    this.keyboardListener.remove()
    this.teardown()

    if (
      this.dimensionsEventListener &&
      this.dimensionsEventListener.remove &&
      typeof this.dimensionsEventListener.remove === 'function'
    ) {
      this.dimensionsEventListener.remove()
    } else {
      // TODO(michael-adalo): deprecated, remove after new React Native is released
      Dimensions.removeEventListener('change', this.screenSizeChange)
    }
  }

  screenSizeChange = () => {
    this.forceUpdate()
  }

  componentDidUpdate(prevProps, prevState) {
    const { component, params, formInputs, topScreen } = this.props
    const { updateContext } = this.context
    const { updateVersion } = this.state

    const inputsDifferent = getInputsDifferent(
      prevProps.component,
      prevProps.formInputs,
      formInputs
    )

    if (
      prevProps.topScreen !== topScreen &&
      !prevProps.topScreen &&
      this.previouslyMounted
    ) {
      this.backActionTaken = true
    }

    if (
      topScreen &&
      (prevProps.component !== component ||
        prevProps.params !== params ||
        prevProps.topScreen !== topScreen ||
        inputsDifferent ||
        prevState.updateVersion !== updateVersion)
    ) {
      updateContext(this.getChild(), this.setContextConfigured.bind(this))
      this.inputsDifferent = inputsDifferent
      this.setState({
        offset: 0,
        reachedPaginationEnd: false,
        initialRender: true,
      })
      this.setup()
    }

    if (topScreen && !prevProps.topScreen) {
      this.teardown()
    }
  }

  getStatusBarHidden = () => {
    const { component } = this.props
    const { statusBarStyle } = component

    if (statusBarStyle === statusBarStyles.HIDDEN) {
      return true
    }

    return false
  }

  getBarStyle = () => {
    const { component } = this.props
    const { statusBarStyle } = component

    if (statusBarStyle === statusBarStyles.LIGHT_CONTENT) {
      return 'light-content'
    } else if (statusBarStyle === statusBarStyles.DARK_CONTENT) {
      return 'dark-content'
    }
  }

  handleScroll = e => {
    const shouldCheckPagination = isCloseToBottom(e.nativeEvent)
    if (shouldCheckPagination) {
      this.checkPagination()
    }
  }

  setScrollEnabled = s => {
    this.setState({ scrollEnabled: s })
  }

  checkPagination = async () => {
    const {
      loading,
      reachedPaginationEnd,
      offset: prevOffset,
      initialRender,
    } = this.state
    const { component } = this.props

    const offset = initialRender ? prevOffset : prevOffset + 20

    const dependencies = (await this.getDependencies()).filter(dep => {
      const { paginate, limit, isExternalCollection } = dep

      // Dependencies for Table objects are handled by the Table & PaginatedFetchComponent classes
      if (!isScreenManagedDependency(component, dep)) {
        return false
      }

      // Only fetch external collections one time if it's not paginated
      if (
        (!initialRender && isExternalCollection && !paginate) ||
        isExternalCollection
      ) {
        return false
      }

      let belowLimit = true

      if (limit) {
        belowLimit = offset < Number(limit) + 20
      }

      return paginate && belowLimit
    })

    if (loading || reachedPaginationEnd || dependencies.length === 0) {
      return
    }

    this.setState({ offset, loading: true })

    const promises = dependencies.map(dep => this.fetchDependency(dep, offset))

    const results = await Promise.all(promises)

    const positiveResults = results.filter(
      r => r && r.value && r.value.data && r.value.data.length >= 20
    )

    if (positiveResults.length === 0) {
      this.setState({
        reachedPaginationEnd: true,
      })
    }

    setTimeout(() => {
      this.setState({ loading: false, initialRender: false })
    }, 100)
  }

  handleResize = (width, height) => {
    if (typeof width !== 'number') {
      ;({ width } = Dimensions.get('window'))
    }

    const { loaded, deviceType: oldDeviceType } = this.state
    const { component } = this.props
    const { getApp } = this.context
    const app = getApp()

    if (height) {
      this._innerHeight = height

      if (this._loaded && height < this._scrollViewHeight) {
        this.checkPagination()
      }
    }

    const deviceType = getDeviceType(width, app)
    if (this.isResponsiveWebApp() && oldDeviceType !== deviceType) {
      this.addDeviceChangedUpdate(deviceType)
      this.setState({ deviceType })
    }

    if (this._scrollView && component.reverseScroll) {
      window.setTimeout(() => {
        this._scrollView.scrollToEnd({ animated: loaded })
      }, 10)
    }
  }

  handleLayout = ({ nativeEvent }) => {
    this._scrollViewHeight = nativeEvent.layout.height
  }

  scrollViewRef = el => {
    this._scrollView = el
  }

  scrollTo = async (y = 0) => {
    setTimeout(() => {
      this._scrollView.scrollTo({ x: 0, y, animated: true })
    }, 200)
  }

  isListView = () => {
    const { component } = this.props
    const body = component.layout.body[0]

    if (!body) {
      return false
    }

    const bodyChildren = body.children || []
    const listChildren = bodyChildren.filter(obj => isListObject(obj))

    return listChildren.length === 1 && !this.isResponsiveWebApp()
  }

  get useFlatList() {
    return this.isListView()
  }

  render() {
    const { component, topScreen, layoutGuides, screenHeight, previewType } =
      this.props

    const {
      loaded,
      loading,
      refreshing,
      scrollEnabled,
      dimScreenWidth,
      dimScreenHeight,
      isPWA,
      contextConfigured,
    } = this.state
    const { getApp, getAssetURL } = this.context

    const app = getApp()

    let {
      backgroundColor,
      backgroundImage,
      backgroundSize,
      backgroundPositionX,
      backgroundPositionY,
      layout,
    } = component

    const isResponsiveWebApp = this.isResponsiveWebApp()

    const branding = (app && app.branding) || defaultBranding

    const extraParams = { fit: 'crop' }
    if (dimScreenWidth && typeof dimScreenWidth === 'number') {
      extraParams.w = dimScreenWidth
    }
    if (dimScreenHeight && typeof dimScreenHeight === 'number') {
      extraParams.h = dimScreenHeight
    }

    backgroundImage =
      backgroundImage && getAssetURL(backgroundImage, extraParams)

    backgroundPositionX = backgroundPositionX || 'center'
    backgroundPositionY = backgroundPositionY || 'center'
    backgroundSize = backgroundSize || 'cover'

    const offsetTop = layoutGuides.top - 20

    const offsetBottom = layoutGuides.bottom

    const androidAvoidingBehavior = isResponsiveWebApp ? 'height' : undefined
    const avoidingBehavior =
      Platform.OS === 'android' ? androidAvoidingBehavior : 'padding'

    const paddingStyles = {
      paddingTop: Math.max(0, offsetTop),
      paddingBottom: offsetBottom,
    }

    const loadingStyles = {
      position: 'fixed',
      top: 0,
      width: '100%',
      height: '100%',
      display: 'flex',
    }

    if (offsetTop < 0 && isMobileWeb()) {
      paddingStyles.top = offsetTop
    }

    if (Platform.OS !== 'web' && isResponsiveWebApp) {
      // Android returns a positive number
      // iOS returns null
      let statusBarHeight = Math.floor(StatusBar.currentHeight)

      const hasDynamicIslandOrNotch =
        DeviceInfo.hasDynamicIsland() || DeviceInfo.hasNotch()
      if (!statusBarHeight) {
        // ios devices don't have a status bar height, so we need to calculate it
        // based on the device height, devices greater than 800 are iphones with
        // a notch, so we need to use 44, otherwise use 20
        statusBarHeight = hasDynamicIslandOrNotch ? 44 : 20
      }

      // TODO(dyego) remove the -20 from the calculation once we start saving top fixed components
      // with a "y" value of 0 instead of 20
      paddingStyles.paddingTop = statusBarHeight - 20

      paddingStyles.paddingBottom = 0

      if (Platform.OS === 'ios') {
        // if statusBarHeight is 44 it means that it's an iphone which contains a notch on the bottom too
        // if it's not then it doesn't have a notch on the bottom, so we need to set the bottom to 0
        paddingStyles.paddingBottom = hasDynamicIslandOrNotch ? 20 : 0
      }
    }

    if (isPWA) {
      const userAgent = navigator.userAgent || navigator.vendor

      // only apply such offset for iOS devices
      if (/iPad|iPhone|iPod/.test(userAgent)) {
        paddingStyles.paddingBottom += 30
      }
    }

    const heightStyles = {}

    const scrollViewInnerStyles = {
      paddingBottom: previewType === 'web' ? offsetBottom : offsetBottom + 20,
    }

    const backgroundStyles = {
      backgroundColor: normalizeColor(
        backgroundColor || '@background',
        branding
      ),
    }

    let screenBody = null

    if (!this.useFlatList) {
      screenBody = layout.body.map(obj => (
        <ObjectRenderer
          key={obj.id}
          object={obj}
          component={component}
          active={loaded && topScreen}
          scrollTo={this.scrollTo}
          topScreen={topScreen}
          setScrollEnabled={this.setScrollEnabled}
          isResponsiveComponent={isResponsiveWebApp}
          previewType={previewType}
          layoutType="body"
          setListItems={this.setListItems}
        />
      ))
    }

    const screenFixed = layout.fixed.map(obj => {
      obj.attributes.height = '100%'
      obj.layout.height = '100%'

      return (
        <ObjectRenderer
          key={obj.id}
          object={obj}
          component={component}
          active={loaded && topScreen}
          topScreen={topScreen}
          isResponsiveComponent={isResponsiveWebApp}
          previewType={previewType}
          layoutType="fixed"
          setListItems={this.setListItems}
        />
      )
    })

    let screenHeightStyles = {}
    if (isResponsiveWebApp) {
      const marginBottom = layout.fixed[0]?.children.reduce(
        (currentValue, item) =>
          getMarginBottom(currentValue, item, component, app),
        0
      )

      screenHeightStyles = {
        height: screenHeight,
        minHeight: '100%',
        marginBottom,
      }
    }

    const ContentContainer = USE_VIRTUAL_LIST_CONTAINER
      ? VirtualizedList
      : ScrollView

    if (Platform.OS !== 'web') {
      styles.fixedContent = {
        ...styles.fixedContent,
      }
    }

    return (
      <View style={[styles.root, styles.wrapper, backgroundStyles]}>
        {backgroundImage && (
          <BackgroundImage
            source={backgroundImage}
            resizeMode={backgroundSize}
            positionX={backgroundPositionX}
            positionY={backgroundPositionY}
          />
        )}
        {topScreen && ( // only render status bar on top screen
          <StatusBar
            translucent
            backgroundColor="rgba(0, 0, 0, 0)"
            barStyle={this.getBarStyle()}
            hidden={this.getStatusBarHidden()}
          />
        )}
        <View style={[styles.wrapper]}>
          <KeyboardAvoidingView
            behavior={avoidingBehavior}
            style={styles.wrapper}
          >
            {this.useFlatList ? (
              <ListBody
                body={layout.body}
                style={{ ...paddingStyles, ...scrollViewInnerStyles }}
                component={component}
                active={loaded && topScreen}
                topScreen={topScreen}
                onEndReached={this.checkPagination}
                loading={loading}
                onRefresh={this.handleRefresh}
                refreshing={refreshing}
              />
            ) : (
              <ContentContainer
                ref={this.scrollViewRef}
                keyboardShouldPersistTaps="handled"
                style={[styles.scrollView]}
                showsVerticalScrollIndicator={false}
                showsHorizontalScrollIndicator={false}
                onContentSizeChange={this.handleResize}
                onLayout={this.handleLayout}
                onScroll={this.handleScroll}
                scrollEventThrottle={200}
                scrollEnabled={scrollEnabled}
              >
                <View style={[paddingStyles, scrollViewInnerStyles]}>
                  {!contextConfigured ? (
                    <View style={[loadingStyles]}>
                      <Loading />
                    </View>
                  ) : (
                    <View style={[screenHeightStyles]}>{screenBody}</View>
                  )}
                </View>
              </ContentContainer>
            )}
          </KeyboardAvoidingView>
        </View>
        <View
          style={[styles.fixedContent, paddingStyles, heightStyles]}
          pointerEvents="box-none"
        >
          <KeyboardAvoidingView
            behavior={avoidingBehavior}
            style={[styles.wrapper, styles.offset]}
            pointerEvents="box-none"
            enabled={component.keyboardBehavior === keyboardBehaviors.AVOID}
          >
            {screenFixed}
          </KeyboardAvoidingView>
        </View>
      </View>
    )
  }
}

// TODO(enzo): part of testing VirtualizedList to not have a ScrollView with FlatLists nested
const VirtualizedList = React.forwardRef(({ children, ...listProps }, ref) => (
  <FlatList
    {...listProps}
    ref={ref}
    data={[]}
    keyExtractor={() => 'key'}
    renderItem={null}
    ListHeaderComponent={<>{children}</>}
  />
))

const styles = StyleSheet.create({
  root: {
    overflow: 'hidden',
  },
  wrapper: {
    flex: 1,
  },
  offset: {
    ...Platform.select({
      web: {
        paddingBottom: SafeAreaInsets.bottom,
      },
    }),
  },
  scrollView: {
    flex: 1,
  },
  fixedContent: {
    zIndex: 1,
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    right: 0,
  },
})

const mapStateToProps = (state, { component }) => {
  const wrapper = component.layout?.body?.[0]

  const { layoutVersion } = component

  const isResponsiveWebApp = layoutVersion === RESPONSIVE_WEB_APPS_ALPHA_VERSION

  const screenHeight = isResponsiveWebApp
    ? getNodeHeight(state, component.id, wrapper?.id)
    : 0

  return {
    screenHeight,
    formInputs: getFormInputs(state),
    dataMap: getScreenPushTree(state, component.id),
  }
}

export default connect(mapStateToProps, {
  fetch,
  setCurrentLocation,
  generateNewPushTree: generatePushTree,
})(Screen)
